import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import Fuse from "fuse.js";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  HelpCircle,
  Search,
  Sun,
  ShieldCheck,
  Sparkles,
  Building2,
  Wrench,
  X,
} from "lucide-react";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import { faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { Reveal } from "@/components/Reveal";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

// Hämta alla FAQ från sajtens olika sidor.
import { FAQS as FAQ_DEKORFILM } from "./dekorfilm.index";
import { FAQS as FAQ_FROSTAT } from "./frostat-glas-vs-dekorfolie";
import { FAQS as FAQ_OM_OSS } from "./om-oss";
import { FAQS as FAQ_RADGIVNING } from "./radgivning";
import { FAQS as FAQ_SAK_BUTIK } from "./sakerhetsfilm-butik";
import { FAQS as FAQ_SAK_KONTOR } from "./sakerhetsfilm-kontor";
import {
  FAQS as FAQ_SAK_INDEX,
  COMBO_FAQS as FAQ_SAK_COMBO,
  CLEAR_FAQS as FAQ_SAK_CLEAR,
} from "./sakerhetsfilm.index";
import { FAQS as FAQ_SAK_SOLAR } from "./sakerhetsfilm.solar-screen";
import { FAQS as FAQ_PRESTIGE_VS_SOLAR } from "./solfilm-3m-prestige-vs-solar-screen";
import { FAQS as FAQ_FORETAG } from "./solfilm-foretag.index";
import { FAQS as FAQ_PRIVAT } from "./solfilm-privat";
import { faqs as FAQ_BAST_I_TEST } from "./solfilm-bast-i-test";
import { faqs as FAQ_UV } from "./uv-skydd-varmereduktion";

type CategoryId = "solfilm" | "sakerhetsfilm" | "dekorfilm" | "process";

type CategoryDef = {
  id: CategoryId;
  label: string;
  icon: typeof Sun;
  desc: string;
};

type AggregatedFaq = FaqItem & {
  category: CategoryId;
  source: string;
  sourceTo: string;
  key: string;
};

const CATEGORIES: CategoryDef[] = [
  {
    id: "solfilm",
    label: "Solfilm, värme & UV",
    icon: Sun,
    desc: "TSER, dagsljus, UV-skydd, livslängd, val mellan 3M och Solar Screen.",
  },
  {
    id: "sakerhetsfilm",
    label: "Säkerhets- & splitterskydd",
    icon: ShieldCheck,
    desc: "EN 12600, EN 356, inbrottsskydd, butik och kontor.",
  },
  {
    id: "dekorfilm",
    label: "Dekor- & frostfilm",
    icon: Sparkles,
    desc: "Manifestation, GDPR-avskärmning, logotyp, frostat vs dekorfolie.",
  },
  {
    id: "process",
    label: "Process, pris & företaget",
    icon: Wrench,
    desc: "Om rådgivning, installation, garanti, certifiering och oss på BBN Nordic.",
  },
];

const SOURCES: Array<{
  category: CategoryId;
  source: string;
  sourceTo: string;
  items: FaqItem[];
}> = [
  { category: "solfilm", source: "Solfilm för företag", sourceTo: "/solfilm-foretag", items: FAQ_FORETAG },
  { category: "solfilm", source: "Solfilm för privatpersoner", sourceTo: "/solfilm-privat", items: FAQ_PRIVAT },
  { category: "solfilm", source: "UV-skydd & värmereducering", sourceTo: "/uv-skydd-varmereduktion", items: FAQ_UV },
  { category: "solfilm", source: "Solfilm — bäst i test", sourceTo: "/solfilm-bast-i-test", items: FAQ_BAST_I_TEST },
  {
    category: "solfilm",
    source: "3M Prestige vs Solar Screen",
    sourceTo: "/solfilm-3m-prestige-vs-solar-screen",
    items: FAQ_PRESTIGE_VS_SOLAR,
  },

  { category: "sakerhetsfilm", source: "Säkerhetsfilm — översikt", sourceTo: "/sakerhetsfilm", items: FAQ_SAK_INDEX },
  { category: "sakerhetsfilm", source: "Säkerhetsfilm — kombinationer", sourceTo: "/sakerhetsfilm", items: FAQ_SAK_COMBO },
  { category: "sakerhetsfilm", source: "Säkerhetsfilm — klar splitterskydd", sourceTo: "/sakerhetsfilm", items: FAQ_SAK_CLEAR },
  { category: "sakerhetsfilm", source: "Säkerhetsfilm — Solar Screen", sourceTo: "/sakerhetsfilm/solar-screen", items: FAQ_SAK_SOLAR },
  { category: "sakerhetsfilm", source: "Säkerhetsfilm för butik", sourceTo: "/sakerhetsfilm-butik", items: FAQ_SAK_BUTIK },
  { category: "sakerhetsfilm", source: "Säkerhetsfilm för kontor", sourceTo: "/sakerhetsfilm-kontor", items: FAQ_SAK_KONTOR },

  { category: "dekorfilm", source: "Dekorfilm — översikt", sourceTo: "/dekorfilm", items: FAQ_DEKORFILM },
  { category: "dekorfilm", source: "Frostat glas vs dekorfolie", sourceTo: "/frostat-glas-vs-dekorfolie", items: FAQ_FROSTAT },

  { category: "process", source: "Rådgivning & process", sourceTo: "/radgivning", items: FAQ_RADGIVNING },
  { category: "process", source: "Om BBN Nordic", sourceTo: "/om-oss", items: FAQ_OM_OSS },
];

const ALL_FAQS: AggregatedFaq[] = (() => {
  const seen = new Set<string>();
  const out: AggregatedFaq[] = [];
  for (const block of SOURCES) {
    for (const item of block.items) {
      // Deduplicera identiska frågor som förekommer på flera sidor.
      const dedupeKey = item.q.trim().toLowerCase();
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({
        ...item,
        category: block.category,
        source: block.source,
        sourceTo: block.sourceTo,
        key: `${block.sourceTo}::${item.q}`,
      });
    }
  }
  return out;
})();

const FAQ_COUNT = ALL_FAQS.length;

// Synonymer / vanliga felstavningar → expanderar söktermen.
const SYNONYMS: Record<string, string[]> = {
  brf: ["bostadsrätt", "bostadsratt", "förening", "förening"],
  bostadsrätt: ["brf"],
  sol: ["solfilm", "solskydd"],
  solfilm: ["sol", "solskydd", "tser"],
  värme: ["varme", "tser", "värmereducering"],
  varme: ["värme"],
  uv: ["uv-skydd", "ultraviolett", "blekning"],
  säkerhet: ["sakerhet", "säkerhetsfilm", "inbrott", "en12600", "en 12600"],
  sakerhet: ["säkerhet"],
  inbrott: ["säkerhet", "skydd", "en 356"],
  splitter: ["splitterskydd", "säkerhet"],
  frost: ["frostat", "frostfilm", "dekor"],
  dekor: ["dekorfilm", "frost", "logotyp"],
  pris: ["priser", "kostnad", "offert"],
  kostnad: ["pris"],
  garanti: ["produktgaranti", "10 år"],
  installation: ["montering", "installatör"],
  prestige: ["3m", "3m prestige"],
  "3m": ["prestige"],
  solar: ["solar screen"],
  "solar screen": ["solar"],
};

function expandQuery(raw: string): string {
  const q = raw.trim().toLowerCase();
  if (!q) return q;
  const tokens = q.split(/\s+/).filter(Boolean);
  const expanded = new Set<string>(tokens);
  for (const t of tokens) {
    const syns = SYNONYMS[t];
    if (syns) syns.forEach((s) => expanded.add(s));
  }
  return Array.from(expanded).join(" ");
}

const FUSE = new Fuse(ALL_FAQS, {
  keys: [
    { name: "q", weight: 0.6 },
    { name: "a", weight: 0.3 },
    { name: "source", weight: 0.1 },
  ],
  threshold: 0.4, // tål felstavningar
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 2,
  useExtendedSearch: false,
});


export const Route = createFileRoute("/vanliga-fragor")({
  head: () => ({
    meta: [
      { title: "FAQ — Vanliga frågor om fönsterfilm, solfilm, säkerhetsfilm & dekorfilm | BBN Nordic" },
      {
        name: "description",
        content:
          "Hela kunskapsbanken på en sida — solfilm, säkerhetsfilm, dekorfilm, garanti och installation. Korta, källbaserade svar grundade i 3M, Solar Screen och svenska standarder (EN 12600, EN 356).",
      },
      { property: "og:title", content: "FAQ — Vanliga frågor om fönsterfilm | BBN Nordic" },
      {
        property: "og:description",
        content: `${FAQ_COUNT}+ frågor och svar om solfilm, säkerhetsfilm, dekorfilm, garanti och installation — samlade på en sida.`,
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://bbnnordic.se/vanliga-fragor" },
      { property: "og:image", content: IMAGES.solfilmHero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/vanliga-fragor" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(ALL_FAQS.map(({ q, a }) => ({ q, a })))),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<CategoryId | "all">("all");

  const filtered = useMemo(() => {
    const raw = query.trim();
    const base = activeCategory === "all"
      ? ALL_FAQS
      : ALL_FAQS.filter((f) => f.category === activeCategory);
    if (!raw) return base;
    const expanded = expandQuery(raw);
    const results = FUSE.search(expanded).map((r) => r.item);
    return results.filter((f) => activeCategory === "all" || f.category === activeCategory);
  }, [query, activeCategory]);

  // Förslag när inga träffar — slå med större tolerans + visa toppfrågor.
  const suggestions = useMemo(() => {
    const raw = query.trim();
    if (!raw || filtered.length > 0) return [];
    const looseFuse = new Fuse(ALL_FAQS, {
      keys: ["q"],
      threshold: 0.6,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
    return looseFuse.search(expandQuery(raw)).slice(0, 5).map((r) => r.item);
  }, [query, filtered.length]);


  const grouped = useMemo(() => {
    const map = new Map<CategoryId, AggregatedFaq[]>();
    for (const f of filtered) {
      const arr = map.get(f.category) ?? [];
      arr.push(f);
      map.set(f.category, arr);
    }
    return map;
  }, [filtered]);

  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="relative pt-24 pb-12 md:pt-40 md:pb-20 bg-navy overflow-hidden">
        <HeroBgImage src={BBN_PHOTOS.installationReference} alt="BBN Nordic montörer installerar 3M fönsterfilm — vanliga frågor" />
        <div className="relative mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              <HelpCircle size={14} />
              <span>Kunskapsbank · {FAQ_COUNT} frågor</span>
            </div>
            <h1 className="font-display text-cream text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
              Alla frågor.<br />
              <em className="italic text-gold">Ett ställe.</em>
            </h1>
            <p className="mt-6 max-w-2xl text-cream/75 text-base md:text-lg font-light leading-relaxed">
              Här hittar du svar på återkommande frågor om solfilm, säkerhetsfilm,
              dekorfilm, garanti och installation. Vi utgår från <strong className="text-cream">3M:s och Solar Screens officiella produktdata</strong>{" "}
              och svenska standarder (EN 12600, EN 356, AFS 2020:1).
            </p>
          </Reveal>

          {/* Search */}
          <Reveal delay={0.1}>
            <div className="relative mt-10">
              <Search
                size={18}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-cream/75"
                aria-hidden
              />
              <label htmlFor="faq-search" className="sr-only">
                Sök bland alla frågor
              </label>
              <input
                id="faq-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Sök t.ex. TSER, EN 356, garanti, BRF, pris…"
                className="w-full bg-cream/5 border border-cream/15 hover:border-cream/30 focus:border-gold focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-gold/30 text-cream placeholder:text-cream/70 rounded-2xl pl-12 pr-12 py-4 md:py-5 text-base font-light transition-colors"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Rensa sökning"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full flex items-center justify-center text-cream/80 hover:text-cream hover:bg-cream/10 transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </Reveal>

          {/* Category chips */}
          <Reveal delay={0.15}>
            <div className="mt-6 flex flex-wrap gap-2">
              <CategoryChip
                active={activeCategory === "all"}
                onClick={() => setActiveCategory("all")}
                count={ALL_FAQS.length}
              >
                Alla
              </CategoryChip>
              {CATEGORIES.map((c) => {
                const count = ALL_FAQS.filter((f) => f.category === c.id).length;
                const Icon = c.icon;
                return (
                  <CategoryChip
                    key={c.id}
                    active={activeCategory === c.id}
                    onClick={() => setActiveCategory(c.id)}
                    count={count}
                  >
                    <Icon size={14} className="opacity-80" />
                    {c.label}
                  </CategoryChip>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <PostHeroConversion />

      {/* FAQ-listor */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          {filtered.length === 0 ? (
            <EmptyState
              query={query}
              suggestions={suggestions}
              onPickSuggestion={(s) => setQuery(s)}
              onClear={() => { setQuery(""); setActiveCategory("all"); }}
            />
          ) : (
            <div className="space-y-16 md:space-y-24">
              {CATEGORIES.map((c) => {
                const items = grouped.get(c.id);
                if (!items || items.length === 0) return null;
                const Icon = c.icon;
                return (
                  <div key={c.id} id={c.id} className="scroll-mt-28">
                    <Reveal>
                      <div className="flex items-start gap-4 mb-2">
                        <div className="shrink-0 rounded-2xl bg-gold/15 ring-1 ring-gold/30 p-3 mt-1">
                          <Icon size={22} className="text-gold" />
                        </div>
                        <div className="min-w-0">
                          <p className="section-number">{items.length} frågor</p>
                          <h2 className="mt-2 font-display text-3xl md:text-4xl text-foreground">
                            {c.label}
                          </h2>
                          <p className="mt-2 text-sm md:text-base text-muted-foreground font-light">
                            {c.desc}
                          </p>
                        </div>
                      </div>
                    </Reveal>

                    <div className="mt-8 divide-y divide-border border-y border-border">
                      {items.map((f, i) => (
                        <FaqRow key={f.key} item={f} index={i} highlight={query} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16 md:py-24 border-y border-gold/20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold-bright font-semibold mb-5">
            <ShieldCheck size={14} /> Auktoriserad 3M-installatör · 10 års garanti
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-cream mb-5">
            Hittade du inte svaret?
          </h2>
          <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed font-light">
            Skriv din fråga i offertformuläret. Vi hör av oss samma arbetsdag och kommer
            gärna ut för en kostnadsfri mätning på plats.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/offert"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
            >
              Ställ din fråga / be om offert <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-6 py-4 rounded-full font-semibold hover:bg-gold/10 transition min-h-12"
            >
              <Phone size={16} /> 0771-502 502
            </a>
          </div>
        </div>
      </section>

      {/* Källor / utforska vidare */}
      <section className="py-16 md:py-20 bg-card/30 border-b border-border">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Källor till svaren</p>
            <h2 className="mt-3 font-display text-2xl md:text-3xl text-foreground">
              Läs mer på sidorna som svaren kommer från
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SOURCES.filter((s, i, arr) => arr.findIndex((x) => x.sourceTo === s.sourceTo) === i).map((s, i) => {
              const cat = CATEGORIES.find((c) => c.id === s.category)!;
              const Icon = cat.icon;
              return (
                <Reveal key={s.sourceTo} delay={i * 0.04}>
                  <Link
                    to={s.sourceTo}
                    className="group block h-full rounded-2xl border border-border bg-card hover:border-gold/50 hover:bg-card/80 transition-colors p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        <Icon size={14} className="text-gold" />
                        {cat.label.split(",")[0]}
                      </div>
                      <ArrowUpRight
                        size={18}
                        className="text-gold shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </div>
                    <h3 className="mt-3 font-display text-base md:text-lg text-foreground leading-snug">
                      {s.source}
                    </h3>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

function CategoryChip({
  children,
  active,
  onClick,
  count,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
        active
          ? "bg-gold text-navy border-gold shadow-[0_8px_24px_-12px_rgba(196,151,58,0.7)]"
          : "bg-cream/5 text-cream/85 border-cream/15 hover:border-cream/35 hover:text-cream"
      }`}
    >
      {children}
      <span
        className={`ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
          active ? "bg-navy/15 text-navy" : "bg-cream/10 text-cream/75"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function FaqRow({
  item,
  index,
  highlight,
}: {
  item: AggregatedFaq;
  index: number;
  highlight: string;
}) {
  return (
    <details className="group" open={index === 0 && !highlight ? true : undefined}>
      <summary className="flex w-full cursor-pointer list-none items-start justify-between gap-6 py-5 md:py-6 text-left [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <h3 className="text-base md:text-lg font-display text-foreground group-hover:text-gold transition-colors">
            {item.q}
          </h3>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
            <Building2 size={11} className="text-gold/70" />
            <span className="truncate">Från: {item.source}</span>
          </div>
        </div>
        <span className="mt-1 shrink-0 flex h-8 w-8 items-center justify-center rounded-full border border-border text-gold transition-transform group-open:rotate-45">
          <span className="text-lg leading-none" aria-hidden>
            +
          </span>
        </span>
      </summary>
      <div className="pb-6 pr-2 md:pr-12 text-muted-foreground leading-relaxed font-light whitespace-pre-line">
        {item.a}
        <div className="mt-4">
          <Link
            to={item.sourceTo}
            className="inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold-bright underline-offset-4 hover:underline transition-colors"
          >
            Läs mer på {item.source}
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </details>
  );
}

function EmptyState({
  query,
  suggestions,
  onPickSuggestion,
  onClear,
}: {
  query: string;
  suggestions: AggregatedFaq[];
  onPickSuggestion: (q: string) => void;
  onClear: () => void;
}) {
  const hasSuggestions = suggestions.length > 0;
  return (
    <div className="text-center py-16 md:py-20 border border-dashed border-border rounded-3xl px-6">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold/10 text-gold mb-4">
        <Search size={20} />
      </div>
      <h2 className="font-display text-2xl md:text-3xl text-foreground">
        {hasSuggestions ? "Inga exakta träffar" : "Ingen fråga matchar"}
        {query && <span className="text-muted-foreground"> för "{query}"</span>}
      </h2>
      <p className="mt-3 text-muted-foreground max-w-md mx-auto font-light">
        {hasSuggestions
          ? "Menade du något av detta? Klicka för att söka direkt."
          : "Prova ett bredare sökord eller välj en annan kategori. Är frågan helt utanför det vi hunnit täcka — ring oss eller skriv i offertformuläret."}
      </p>

      {hasSuggestions && (
        <ul className="mt-6 mx-auto max-w-2xl flex flex-col gap-2 text-left">
          {suggestions.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => onPickSuggestion(s.q)}
                className="w-full flex items-start gap-3 rounded-xl border border-border bg-card hover:border-gold/60 hover:bg-card/80 transition-colors px-4 py-3 text-left"
              >
                <Search size={14} className="mt-1 shrink-0 text-gold" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm md:text-base font-medium text-foreground">
                    {s.q}
                  </span>
                  <span className="block mt-0.5 text-xs text-muted-foreground truncate">
                    Från: {s.source}
                  </span>
                </span>
                <ArrowUpRight size={16} className="shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-medium hover:border-gold hover:text-gold transition-colors"
        >
          Rensa filter
        </button>
        <Link
          to="/offert"
          className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 rounded-full text-sm font-semibold"
        >
          Ställ frågan direkt <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
